'use client';

import { AlertTriangle, AlertCircle, Info } from 'lucide-react';
import { Button } from '@/shared/components/Button';
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from '@/shared/components/Dialog';
import { useGlobalDialogStore } from '@/shared/stores/useGlobalDialogStore';

const variantConfig = {
	warning: {
		icon: AlertTriangle,
		className:
			'bg-yellow-500/20 border-yellow-400/30 text-yellow-400 shadow-yellow-500/20',
	},
	error: {
		icon: AlertCircle,
		className:
			'bg-red-500/20 border-red-400/30 text-red-400 shadow-red-500/20',
	},
	info: {
		icon: Info,
		className:
			'bg-blue-500/20 border-blue-400/30 text-blue-400 shadow-blue-500/20',
	},
};

export function GlobalDialog() {
	const { isOpen, config, closeDialog } = useGlobalDialogStore();

	if (!config) return null;

	const { icon: Icon, className: iconClassName } =
		variantConfig[config.variant];

	const handlePrimaryAction = async () => {
		await config.primaryAction.handler();
		closeDialog();
	};

	const handleSecondaryAction = () => {
		config.secondaryAction?.handler();
		closeDialog();
	};

	return (
		<Dialog open={isOpen} onOpenChange={(open) => !open && closeDialog()}>
			<DialogContent>
				<DialogHeader>
					<div className="flex items-center gap-3">
						<div
							className={`w-12 h-12 shrink-0 rounded-full backdrop-blur-sm flex items-center justify-center border shadow-lg ${iconClassName}`}
						>
							<Icon className="w-6 h-6" />
						</div>
						<div>
							<DialogTitle>{config.title}</DialogTitle>
							<DialogDescription className="mt-1">
								{config.description}
							</DialogDescription>
						</div>
					</div>
				</DialogHeader>

				<DialogFooter className="gap-2">
					{config.secondaryAction && (
						<Button
							variant="outline"
							onClick={handleSecondaryAction}
							className="glass-light glass-hover border-white/20 text-white hover:bg-white/10"
						>
							{config.secondaryAction.label}
						</Button>
					)}
					<Button
						variant={
							config.variant === 'warning' ||
							config.variant === 'error'
								? 'destructive'
								: 'default'
						}
						onClick={handlePrimaryAction}
					>
						{config.primaryAction.label}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
